import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import Button from "@atoms/Button/Button"
import "@lang/i18n"
import {
  GoogleMap,
  InfoWindow,
  LoadScriptNext,
  Marker,
  MarkerClusterer,
} from "@react-google-maps/api"
import { supify } from "@utils/helpers"

import styles from "./GoogleMapComponent.module.scss"

const GoogleMapComponent = ({
  markers,
  open,
  setOpen,
  height,
  numbers,
  defaultZoom = 12,
  markerSize = 40,
  setActiveCampus,
  fullscreenControl = false,
}) => {
  const fallbackCoordinates = {
    lat: 59.130863,
    lng: 19.851887,
  }

  const { t } = useTranslation()

  let [openMap, setOpenMap] = useState(markers.map((marker) => false))

  // Override
  if (open && setOpen) {
    openMap = open
    setOpenMap = setOpen
  }

  const [iconMarker, setIconMarker] = useState(false)

  const containerStyle = {
    width: "100%",
    height: height,
  }

  const clusterOptions = {
    gridSize: 10,
    maxZoom: 8,
    averageCenter: true,
    imagePath: "/media/markers/m",
    clusterClass: "marker-cluster",
    styles: [
      {
        textColor: "white",
        textSize: 18,
        url: "/media/markers/m1.png",
        width: 56,
        height: 56,
        fontFamily: "Open Sans",
      },
      {
        textColor: "white",
        textSize: 18,
        url: "/media/markers/m2.png",
        width: 56,
        height: 56,
        fontFamily: "Open Sans",
      },
      {
        textColor: "white",
        textSize: 18,
        url: "/media/markers/m3.png",
        width: 56,
        height: 56,
        fontFamily: "Open Sans",
      },
      {
        textColor: "white",
        textSize: 18,
        url: "/media/markers/m4.png",
        width: 56,
        height: 56,
        fontFamily: "Open Sans",
      },
      {
        textColor: "white",
        textSize: 18,
        url: "/media/markers/m5.png",
        width: 56,
        height: 56,
        fontFamily: "Open Sans",
      },
      {
        textColor: "white",
        textSize: 18,
        url: "/media/markers/m6.png",
        width: 56,
        height: 56,
        fontFamily: "Open Sans",
      },
    ],
  }

  const mapStyles = [
    {
      featureType: "all",
      elementType: "geometry.fill",
      stylers: [{ weight: "2.00" }],
    },
    {
      featureType: "all",
      elementType: "geometry.stroke",
      stylers: [{ color: "#9c9c9c" }],
    },
    {
      featureType: "all",
      elementType: "labels.text",
      stylers: [{ visibility: "on" }],
    },
    {
      featureType: "landscape",
      elementType: "all",
      stylers: [{ color: "#f2f2f2" }],
    },
    {
      featureType: "landscape",
      elementType: "geometry.fill",
      stylers: [{ color: "#ffffff" }],
    },
    {
      featureType: "landscape.man_made",
      elementType: "geometry.fill",
      stylers: [{ color: "#ffffff" }],
    },
    {
      featureType: "landscape.man_made",
      elementType: "geometry.stroke",
      stylers: [{ color: "#000000" }],
    },
    {
      featureType: "poi",
      elementType: "all",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "road",
      elementType: "all",
      stylers: [{ saturation: -100 }, { lightness: 45 }],
    },
    {
      featureType: "road",
      elementType: "geometry.fill",
      stylers: [{ color: "#eeeeee" }],
    },
    {
      featureType: "road",
      elementType: "labels.text.fill",
      stylers: [{ color: "#7b7b7b" }],
    },
    {
      featureType: "road",
      elementType: "labels.text.stroke",
      stylers: [{ color: "#ffffff" }],
    },
    {
      featureType: "road.highway",
      elementType: "all",
      stylers: [{}],
    },
    {
      featureType: "road.arterial",
      elementType: "labels.icon",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "transit",
      elementType: "all",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "water",
      elementType: "all",
      stylers: [{ color: "#343434" }, { visibility: "on" }],
    },
    {
      featureType: "water",
      elementType: "geometry.fill",
      stylers: [{ color: "#f2f2f2" }],
    },
    {
      featureType: "water",
      elementType: "labels.text.fill",
      stylers: [{ color: "#070707" }],
    },
    {
      featureType: "water",
      elementType: "labels.text.stroke",
      stylers: [{ color: "#ffffff" }],
    },
  ]

  const latitudes = markers.map((marker) => marker.position.lat)
  const longitudes = markers.map((marker) => marker.position.lng)

  const defaultCenter = {
    lat: (Math.min(...latitudes) + Math.max(...latitudes)) / 2,
    lng: (Math.min(...longitudes) + Math.max(...longitudes)) / 2,
  }

  const onLoad = React.useCallback(
    function callback(map) {
      if (numbers) {
        let markers = {}

        numbers.forEach((image, index) => {
          let marker = new window.google.maps.MarkerImage(
            image,
            null /* size is determined at runtime */,
            null /* origin is 0,0 */,
            new window.google.maps.Point(
              20,
              36
            ) /* anchor is bottom center of the scaled image */,
            new window.google.maps.Size(markerSize, markerSize)
          )

          markers[index] = marker
        })

        setIconMarker(markers)
      } else {
        let marker = new window.google.maps.MarkerImage(
          "/media/markers/marker.png",
          null /* size is determined at runtime */,
          null /* origin is 0,0 */,
          new window.google.maps.Point(
            20,
            36
          ) /* anchor is bottom center of the scaled image */,
          new window.google.maps.Size(markerSize, markerSize)
        )
        setIconMarker(marker)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const allMarkers = markers.map((marker, index) => {
    const markerContent = marker.internalLink ? (
      <>
        <div className="google-maps-location-content">
          <h3 dangerouslySetInnerHTML={{ __html: supify(marker.title) }}></h3>
          <p>
            <span>{marker.address}</span>
          </p>
          {marker.url && (
            <Button size="small" href={marker.url} internalLink={true}>
              {t("Read more")}
            </Button>
          )}
        </div>
      </>
    ) : (
      <>
        <div className={styles.googleMapsLocationContent}>
          <h3 dangerouslySetInnerHTML={{ __html: supify(marker.title) }}></h3>
          <p>
            <span>{marker.address}</span>
          </p>
          {marker.url && (
            <Button size="small" href={marker.url}>
              {t("Read more")}
            </Button>
          )}
          {marker.listFilter && (
            <Button size="small" onClick={() => setActiveCampus(marker.title)}>
              {t("Filter by this campus")}
            </Button>
          )}
        </div>
      </>
    )

    return (
      <Marker
        key={index}
        icon={numbers ? iconMarker[index] : iconMarker}
        infoWindow={marker.infoWindow}
        markerContent={markerContent}
        position={{
          lat: parseFloat(marker.position.lat),
          lng: parseFloat(marker.position.lng),
        }}
        style={{ filter: "invert(1)" }}
      ></Marker>
    )
  })

  return (
    <LoadScriptNext
      onLoad={onLoad}
      googleMapsApiKey="AIzaSyAGb1TluTIq-OWKvYbDWiyurp33rIx_Tes"
    >
      <GoogleMap
        mapContainerStyle={containerStyle}
        zoom={defaultZoom}
        center={{
          lat: defaultCenter.lat ? defaultCenter.lat : fallbackCoordinates.lat,
          lng: defaultCenter.lng ? defaultCenter.lng : fallbackCoordinates.lng,
        }}
        options={{
          styles: mapStyles,
          zoomControl: true,
          mapTypeControl: false,
          scaleControl: false,
          streetViewControl: false,
          rotateControl: false,
          fullscreenControl: fullscreenControl,
          backgroundColor: "#f2f2f2",
        }}
      >
        <MarkerClusterer options={clusterOptions}>
          {(clusterer) =>
            allMarkers.map((marker, i) => {
              return (
                <Marker
                  key={i}
                  icon={marker.props.icon}
                  position={marker.props.position}
                  style={marker.props.style}
                  onClick={() =>
                    setOpenMap({ ...markers.map((marker) => false), [i]: true })
                  }
                  clusterer={clusterer}
                >
                  {marker.props.infoWindow && openMap[i] && (
                    <InfoWindow
                      onCloseClick={() =>
                        setOpenMap({ ...openMap, [i]: false })
                      }
                    >
                      <div className={styles.infoWindow}>
                        <div className={styles.googleMapsLocation}>
                          {marker.props.markerContent}
                        </div>
                      </div>
                    </InfoWindow>
                  )}
                </Marker>
              )
            })
          }
        </MarkerClusterer>
      </GoogleMap>
    </LoadScriptNext>
  )
}

export default GoogleMapComponent
